import React, {
  Fragment,
  useEffect,
  useState,
} from "react";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";

import { getDashboard, searchGuest } from "../api/dashboard.api";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "../AbstractElements";
import { titleCase } from "../utils/case";
import ReactApexChart from "react-apexcharts";
import DataTable from "react-data-table-component";

const Dashboard = () => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = useState({
    cards: [],
    charts: [],
    tables: [],
    alerts: [],
  });
  const [guestKeyword, setGuestKeyword] = useState("");
  const [guestResult, setGuestResult] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const res = await getDashboard();

      setDashboard(res);
    } finally {
      setLoading(false);
    }
  };

  const [loading, setLoading] = useState(true);

  const buildChartOptions = (chart) => ({
    chart: { toolbar: { show: false }, zoom: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { curve: chart.display === "line" ? "smooth" : "straight", width: 3 },
    xaxis: { categories: chart.labels },
    legend: { show: chart.datasets.length > 1 },
    plotOptions: { bar: { borderRadius: 6, columnWidth: "45%", horizontal: chart.display === "horizontal-bar", } },
    responsive: [{ breakpoint: 768, options: { chart: { height: 280 } } }],
  });

  const handleGuestSearch = async () => {
    if (!guestKeyword.trim()) {
      setGuestResult(null);
      return;
    }

    try {
      setSearching(true);

      const res = await searchGuest(
        guestKeyword
      );

      setGuestResult(res);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("dashboard"))}
        title={titleCase(t("dashboard"))}
        mainTitle={titleCase(t("dashboard"))}
      />
       <Container fluid>
        <Row>
          {dashboard.alerts.map((alert) => (
            <Col
              key={alert.key}
              lg="12"
              className="mb-4"
            >
              <Card>
                <CardBody>
                  <h5>{alert.title}</h5>

                  {alert.items.length ===
                  0 ? (
                    <p className="text-muted">
                      No alerts.
                    </p>
                  ) : (
                    alert.items.map(
                      (item) => (
                        <div
                          key={item.id}
                          className="border-bottom py-2"
                        >
                          {item.name}
                        </div>
                      )
                    )
                  )}
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
        <Row>
          {dashboard.cards.map((card) => (
            <Col
              key={card.key}
              lg="3"
              md="6"
              className="mb-4"
            >
              <Card>
                <CardBody>
                  <div className="text-muted">
                    {card.title}
                  </div>

                  <h2 className="mb-0">
                    {card.display ===
                    "currency"
                      ? `RM ${Number(
                          card.value
                        ).toLocaleString()}`
                      : card.display ===
                        "progress"
                      ? `${card.value}%`
                      : card.value}
                  </h2>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
        <Row>
          {dashboard.charts.map((chart) => {
            const type = {
              line: "line",
              bar: "bar",
              "horizontal-bar": "bar",
              column: "bar",
              area: "area",
              pie: "pie",
              donut: "donut",
            }[chart.display] || "line";

            return (
              <Col
                key={chart.key}
                lg="6"
                className="mb-4"
              >
                <Card className="h-100">
                  <CardBody>
                    <h5 className="mb-4">
                      {chart.title}
                    </h5>

                    <ReactApexChart
                      options={buildChartOptions(chart)}
                      series={chart.datasets.map(
                        (dataset) => ({
                          name: dataset.label,
                          data: dataset.data,
                        })
                      )}
                      type={type}
                      height={320}
                    />
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
        <Row>
          {dashboard.tables.map((widget) => (
            <Col
              key={widget.key}
              lg="12"
              className="mb-4"
            >
              <Card className="h-100">
                <CardBody>
                  <h5 className="mb-4">
                    {widget.title}
                  </h5>

                  {widget.type === "table" && (
                    <DataTable
                      noHeader
                      responsive
                      striped
                      dense
                      highlightOnHover
                      persistTableHead
                      pagination={
                        (widget.rows?.length ?? 0) > 10
                      }
                      columns={widget.columns.map(
                        (column) => ({
                          name: column.label,
                          selector: (row) =>
                            row[column.key],
                          sortable: true,
                          wrap: true,
                        })
                      )}
                      data={widget.rows}
                    />
                  )}

                  {widget.type === "search" && (
                    <>
                      <div className="d-flex gap-2 mb-3">
                        <input
                          className="form-control"
                          placeholder={widget.placeholder}
                          value={guestKeyword}
                          onChange={(e) =>
                            setGuestKeyword(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleGuestSearch();
                            }
                          }}
                        />

                        <button
                          className="btn btn-primary"
                          onClick={handleGuestSearch}
                          disabled={searching}
                        >
                          Search
                        </button>
                      </div>

                      {guestResult &&
                        !guestResult.found && (
                          <div className="alert alert-warning mb-0">
                            No matching reservation found.
                          </div>
                        )}

                      {guestResult?.found && (
                        <Card className="border shadow-none">
                          <CardBody>
                            <Row>
                              <Col md="6">
                                <strong>Booking Ref</strong>
                                <div>
                                  {guestResult.reservation.bookingRef}
                                </div>
                              </Col>

                              <Col md="6">
                                <strong>Status</strong>
                                <div>
                                  {guestResult.reservation.status}
                                </div>
                              </Col>

                              <Col md="6" className="mt-3">
                                <strong>Customer</strong>
                                <div>
                                  {guestResult.reservation.customer}
                                </div>
                              </Col>

                              <Col md="6" className="mt-3">
                                <strong>Lot</strong>
                                <div>
                                  {guestResult.reservation.lot}
                                </div>
                              </Col>

                              <Col md="6" className="mt-3">
                                <strong>Arrival</strong>
                                <div>
                                  {guestResult.reservation.arrival}
                                </div>
                              </Col>

                              <Col md="6" className="mt-3">
                                <strong>Departure</strong>
                                <div>
                                  {guestResult.reservation.departure}
                                </div>
                              </Col>

                              <Col md="6" className="mt-3">
                                <strong>Guests</strong>
                                <div>
                                  {guestResult.reservation.submittedGuests}
                                  {" / "}
                                  {guestResult.reservation.guestQty}
                                </div>
                              </Col>

                              <Col md="6" className="mt-3">
                                <strong>Access Permit</strong>

                                <div>
                                  {guestResult.reservation.accessPermit ? (
                                    <a
                                      href={`${process.env.REACT_APP_FILES_URL}/${guestResult.reservation.accessPermit.filePath}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      View Access Permit
                                    </a>
                                  ) : (
                                    <span className="text-muted">
                                      Not Generated
                                    </span>
                                  )}
                                </div>
                              </Col>
                            </Row>

                            <hr />

                            <h6>Registered Guests</h6>

                            <ul className="mb-0">
                              {guestResult.reservation.guests.map(
                                (guest) => (
                                  <li key={guest.id}>
                                    {guest.name}
                                  </li>
                                )
                              )}
                            </ul>
                          </CardBody>
                        </Card>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
       </Container>
    </Fragment>
  );
};

export default Dashboard;